import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Transition } from "@headlessui/react";
import {
  ExclamationIcon,
  RefreshIcon,
  WifiIcon,
} from "@heroicons/react/outline";
import { isFeatureEnabled } from "src/helpers/utils";
import { reconnectGraphQLWebSocket } from "src/graphql/wsClient";
import useHilosStore from "../../hooks/useHilosStore";

function NetworkWarning() {
  const { t } = useTranslation();
  const { session, isNetworkOnline, hasNetworkError, webSocketState } =
    useHilosStore();

  const allowNetworkError = useMemo(
    () => isFeatureEnabled({ session, key: "allow_network_error" }),
    [session]
  );

  const hasWebSocketFailure =
    webSocketState.graphql.status === "failed" ||
    webSocketState.direct.status === "failed";

  const isWebSocketReconnecting =
    webSocketState.graphql.status === "connecting" ||
    webSocketState.direct.status === "connecting";

  const showWarning =
    !isNetworkOnline ||
    (allowNetworkError && hasNetworkError) ||
    hasWebSocketFailure;

  const handleReconnect = useCallback(() => {
    const { webSocketState, triggerWebSocketReconnect } =
      useHilosStore.getState();

    console.log("[NetworkWarning] Reconnect clicked", webSocketState);

    if (webSocketState.graphql.status === "failed") {
      reconnectGraphQLWebSocket();
    }

    if (webSocketState.direct.status === "failed") {
      triggerWebSocketReconnect();
    }
  }, []);

  const getWarningContent = () => {
    if (!isNetworkOnline) {
      return {
        icon: (
          <WifiIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
        ),
        title: t("inbox:network-disconnected", "Network disconnected"),
        message: t(
          "inbox:network-offline-warning",
          "It appears you're disconnected from our server. Try refreshing this page or wait until we can establish a connection again."
        ),
        showReconnect: false,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-800",
        descColor: "text-yellow-700",
      };
    }

    if (hasWebSocketFailure) {
      return {
        icon: (
          <ExclamationIcon
            className="h-5 w-5 text-red-400"
            aria-hidden="true"
          />
        ),
        title: t("inbox:realtime-connection-lost", "Real-time connection lost"),
        message: t(
          "inbox:realtime-connection-warning",
          "We couldn't maintain the real-time connection. Messages may not update automatically. Click reconnect to try again."
        ),
        showReconnect: true,
        bgColor: "bg-red-50",
        textColor: "text-red-800",
        descColor: "text-red-700",
      };
    }

    if (hasNetworkError) {
      return {
        icon: (
          <svg
            className="h-5 w-5 text-yellow-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            />
          </svg>
        ),
        title: t("inbox:network-error", "Network error"),
        message: t(
          "inbox:network-error-warning",
          "It seems there's an issue connecting to the server. Please check your internet connection or try again later."
        ),
        showReconnect: true,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-800",
        descColor: "text-yellow-700",
      };
    }

    return null;
  };

  const content = getWarningContent();

  if (!content) {
    return null;
  }

  return (
    <Transition
      show={showWarning}
      as={"div"}
      enter="duration-300"
      enterFrom="-translate-y-20"
      enterTo="translate-y-0"
      leave="duration-300"
      leaveFrom="translate-y-0"
      leaveTo="-translate-y-20"
      className="h-auto w-full"
    >
      <div className={`h-full w-full ${content.bgColor} p-4`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">{content.icon}</div>
          <div className="ml-3 flex-1">
            <h3 className={`text-sm font-medium ${content.textColor}`}>
              {content.title}
              {isWebSocketReconnecting && (
                <span className="ml-2 inline-flex items-center">
                  <RefreshIcon className="h-4 w-4 animate-spin" />
                  <span className="ml-1 text-xs font-normal">
                    {t("inbox:reconnecting", "Reconnecting...")}
                  </span>
                </span>
              )}
            </h3>
            <div className={`mt-2 text-sm ${content.descColor}`}>
              <p>{content.message}</p>
              {(webSocketState.graphql.lastError ||
                webSocketState.direct.lastError) && (
                <p className="mt-1 text-xs opacity-75">
                  {t("inbox:error-details", "Details")}:{" "}
                  {webSocketState.graphql.lastError ||
                    webSocketState.direct.lastError}
                </p>
              )}
            </div>
          </div>
          {content.showReconnect && (
            <div className="ml-4 flex-shrink-0">
              <button
                type="button"
                onClick={handleReconnect}
                disabled={isWebSocketReconnecting}
                className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  hasWebSocketFailure
                    ? "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-400"
                    : "bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 disabled:bg-yellow-400"
                }`}
              >
                <RefreshIcon
                  className={`-ml-0.5 mr-2 h-4 w-4 ${
                    isWebSocketReconnecting ? "animate-spin" : ""
                  }`}
                />
                {t("inbox:reconnect", "Reconnect")}
              </button>
            </div>
          )}
        </div>
      </div>
    </Transition>
  );
}

export default NetworkWarning;
